/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { Director, Stage, DefaultCaps, RenderConfig, FunctionalActor, DefaultNavigators, StageDesc, CapNames } from '@sick-davinci/glow';

/**
 * Demo:    Multiview (1)
 * Description:
 *  This demo application shows how to setup a view with one scene and two cameras.
 * Features:
 *  - Orbit navigation with custom settings
 *  - Capability handling
 *  - Multiple viewports
 */
export const run1 = ((viewParent: HTMLElement) => {

  // * Setup the stage *
  const renderConfig =  new RenderConfig();
  renderConfig.enableAlpha = true;

  const setup: StageDesc = {
    cameras: [
      {
        name: 'frontCamera',
        from: [10, 0, 10],
        to: [0, 0, 0],
        up: [0, 1, 0],
        fov: 30,
      },
      {
        name: 'topCamera',
        from: [0, 0, 10],
        to: [0, 0, 0],
        up: [0, 1, 0],
        fov: 30,
      },
    ],
    scenes: [
      { name: 'default' },
    ],
    viewports: [
      {
        scene: 'default',
        camera: 'frontCamera',
        left: 0.01,
        top: 0.01,
        width: 0.48,
        height: 0.98,
        bgcolor: 0x888800,
        alpha: 0.9,
        name: 'left',
      },
      {
        scene: 'default',
        camera: 'topCamera',
        left: 0.5,
        top: 0.0,
        width: 0.5,
        height: 1.0,
        bgcolor: 0x440088,
        alpha: 0.6,
        name: 'right',
      }
      , {
        scene: 'default',
        camera: 'topCamera',
        rightAbs: 50,
        bottomAbs: 50,
        widthAbs: 200,
        heightAbs: 200,
        bgcolor: 0x008888,
        alpha: 0.9,
        name: 'bam',
        parent: 'left',
      },
      {
        name: 'bim',
        scene: 'default',
        camera: 'frontCamera',
        leftAbs: 50,
        topAbs: 50,
        widthAbs: 200,
        heightAbs: 200,
        bgcolor: 0x884400,
        alpha: 0.6,
        parent: 'right',
      },
    ],
  };
  const stage = new Stage(viewParent, setup, renderConfig);

  // * Add an Orbit Navigator
  DefaultNavigators.initOrbit(stage);

  // * Data handling using capabilities
  const director = new Director(stage);
  DefaultCaps.init(director);

  director.handle(CapNames.LIGHT, { payload: { lightType: 'point', color: 0xff8888 }, orientation: { position: [0, 10, 2] } });
  director.handle(CapNames.LIGHT, { payload: { lightType: 'ambient', color: 0xffffff } });

  director.handle(CapNames.TEXT3D, { payload: { text: 'Sick\nDaVinCI', orientation: { scale: [1, 0.5, 1] } } });

  stage.start(); // start visualizing data
});

/**
 * Demo:    Multiview (2)
 * Description:
 *  This demo application shows how to setup a view with two scenes and two cameras.
 * Features:
 *  - Orbit navigation with custom settings
 *  - Capability handling
 *  - Multiple viewports
 *  - Dynamic viewport repositioning
 */
export const run2 = ((viewParent: HTMLElement) => {

  // * Setup the stage *
  const setup: StageDesc = {
    cameras: [
      {
        name: 'frontCamera',
        from: [10, 0, 10],
        to: [0, 0, 0],
        up: [0, 1, 0],
        fov: 30,
      },
      {
        name: 'topCamera',
        from: [0, 0, 10],
        to: [0, 0, 0],
        up: [0, 1, 0],
        fov: 30,
      },
    ],
    scenes: [
      { name: 'default' },
      { name: 'right' },
    ],
    viewports: [
      {
        name: 'main',
        scene: 'default',
        camera: 'frontCamera',
        left: 0.0,
        top: 0.0,
        width: 1.0,
        height: 1.0,
        bgcolor: 0x888800,
      }
      , {
        name: 'child',
        scene: 'right',
        camera: 'topCamera',
        left: 0.7,
        top: 0.1,
        width: 0.2,
        height: 0.2,
        bgcolor: 0x008888,
      },
    ],
  };
  const stage = new Stage(viewParent, setup); // start visualizing data

  // * Add an Orbit Navigator
  DefaultNavigators.initOrbit(stage);

  // * Data handling using capabilities
  const director = new Director(stage);
  DefaultCaps.init(director);

  director.handle(CapNames.LIGHT, { payload: { lightType: 'point', color: 0xff8888 }, orientation: { position: [0, 10, 0] } });
  director.handle(CapNames.LIGHT, { payload: { lightType: 'ambient', color: 0xffffff } });

  director.handle(CapNames.LIGHT, { scene: 'right', payload: { lightType: 'point', color: 0x4444ff }, orientation: { position: [0, 10, 0] } });
  director.handle(CapNames.LIGHT, { scene: 'right', payload: { lightType: 'ambient', color: 0xffffff } });

  director.handle(CapNames.TEXT3D, { payload: { text: 'Sick\nDaVinCI' }, orientation: { scale: [1, 0.5, 1] } });
  director.handle(CapNames.TEXT3D, { scene: 'right', payload: { text: 'Hello World' }, orientation: { scale: [1, 0.5, 1] } });

  stage.start();

  // * Re-position the viewport by time
  let t = 0;
  stage.addActor(new FunctionalActor(() => {
    setup.viewports[1].left = Math.sin(t++ / 500) * 0.35 + 0.35;
    setup.viewports[1].top = Math.cos(t++ / 200) * 0.35 + 0.35;
    stage.changeViewport('child', setup.viewports[1], 0);
  }));

});
